import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { siteConfig, categorySlug } from "@/config";
import { posts } from "@/data/posts";

export const Route = createFileRoute("/search")({
  head: () => ({
    meta: [
      { title: `Search — ${siteConfig.siteName}` },
      { name: "description", content: `Search articles and guides on ${siteConfig.siteName}.` },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: `Search — ${siteConfig.siteName}` },
      { property: "og:description", content: `Search articles and guides on ${siteConfig.siteName}.` },
      { property: "og:url", content: `${siteConfig.domain}/search` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.domain}/search` }],
  }),
  component: SearchPage,
});

function SearchPage() {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const results = useMemo(() => {
    if (!query) return [];
    return posts.filter((p) => {
      const hay = `${p.title} ${p.description} ${p.excerpt} ${p.category} ${p.keywords.join(" ")}`.toLowerCase();
      return hay.includes(query);
    });
  }, [query]);

  return (
    <section className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="mb-2 text-3xl font-bold tracking-tight text-foreground">Search</h1>
      <p className="mb-8 text-muted-foreground">
        Find CRM, ERP, and SaaS reviews across {siteConfig.siteName}.
      </p>

      <label htmlFor="site-search" className="sr-only">Search articles</label>
      <input
        id="site-search"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search articles, categories, keywords…"
        autoFocus
        className="w-full rounded-md border border-input bg-background px-4 py-3 text-base text-foreground shadow-sm outline-none focus:ring-2 focus:ring-ring"
      />

      <div className="mt-8">
        {!query && (
          <p className="text-sm text-muted-foreground">Type a keyword to search.</p>
        )}
        {query && results.length === 0 && (
          <p className="text-sm text-muted-foreground">No results for “{q}”.</p>
        )}
        {results.length > 0 && (
          <>
            <p className="mb-4 text-sm text-muted-foreground">
              {results.length} result{results.length === 1 ? "" : "s"}
            </p>
            <ul className="divide-y divide-border">
              {results.map((p) => (
                <li key={p.id} className="py-4">
                  <Link
                    to="/post/$slug"
                    params={{ slug: p.slug }}
                    className="block group"
                  >
                    <h2 className="text-lg font-semibold text-foreground group-hover:underline">
                      {p.title}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                      {p.excerpt || p.description}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      <Link
                        to="/category/$category"
                        params={{ category: categorySlug(p.category) }}
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {p.category}
                      </Link>
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
